import React, { useRef, useEffect } from 'react';
import { isNil, map } from 'lodash';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Accessibility from 'highcharts/modules/accessibility';
import { chartContextButtonInit } from './chartSettings';

require('highcharts-export-csv')(Highcharts);
require('highcharts-exporting')(Highcharts);
Accessibility(Highcharts);
chartContextButtonInit();

export default function MultiTargetChart({
  advancedMetricChartType,
  height = null,
  measureResults,
  title,
  advancedMetricTargetUnit = '',
  showCSVDownload = true,
  onChartRef,
}) {
  const highchartsRef = useRef(null);

  useEffect(() => {
    if (onChartRef) {
      onChartRef(highchartsRef.current);
    }
  }, [onChartRef]);

  const targets = measureResults.map(({ dataPointYTarget }) => Number(Number(dataPointYTarget).toFixed(2)));
  const outcomes = measureResults.map(({ dataPointYValue }) => {
    if (isNil(dataPointYValue)) {
      return null;
    }

    return Number(Number(dataPointYValue).toFixed(2));
  });
  const categories = map(measureResults, ({ dataPointX }) => dataPointX);
  const series = [
    {
      name: 'Target',
      data: targets,
      pointPadding: 0,
      borderWidth: 0,
      color: '#64d06c',
    },
    {
      name: 'Actual',
      data: outcomes,
      pointPadding: 0,
      borderWidth: 0,
    },
  ];
  const exporting = showCSVDownload
    ? {
        enabled: true,
        csv: {
          columnHeaderFormatter: null,
          dateFormat: '%Y-%m-%d %H:%M:%S',
          decimalPoint: null,
          itemDelimiter: null,
          lineDelimiter: '\n',
        },
        buttons: {
          contextButton: {
            // Internal Highcharts context button disabled — CSV download
            // is now handled by an external React button for accessibility.
            // menuItems: [
            //   {
            //     textKey: 'downloadCSV',
            //     onclick: () => highchartsRef.current.chart.downloadCSV(),
            //   },
            // ],
            // ...contextButtonVisualSettings,
            enabled: false,
          },
        },
      }
    : { enabled: false };
  let type = 'column';

  if (advancedMetricChartType === 'line') {
    type = 'line';
  } else if (advancedMetricChartType === 'area') {
    type = 'area';
  }

  const options = {
    chart: {
      type,
      height,
    },
    title: {
      text: title,
      style: { display: 'none' },
    },
    credits: false,
    tooltip: { shared: true },
    plotOptions: {
      series: {
        dataLabels: {
          enabled: true,
          style: {
            fontWeight: 'none',
            fontSize: '9px',
          },
          allowOverlap: false,
          inside: true,
          verticalAlign: 'middle',
        },
      },
    },
    xAxis: { categories },
    yAxis: {
      labels: {
        format: '{value:,.0f}',
        style: { color: '#404040' },
      },
      title: {
        text: advancedMetricTargetUnit,
        style: { color: '#404040' },
      },
    },
    exporting,
    lang: {
      downloadCSV: 'Download CSV',
      thousandsSep: ',',
    },
    accessibility: {
      enabled: true,
      description: title || 'Chart',
      keyboardNavigation: {
        enabled: true,
        order: ['series', 'zoom', 'rangeSelector', 'legend'],
        focusBorder: { enabled: true, style: { lineWidth: 2, borderRadius: 3 } },
        wrapAround: true,
      },
      screenReaderSection: {
        beforeChartFormat: '<div>{typeDescription}</div><div>{chartLongdesc}</div>',
      },
      series: { describeSingleSeries: true },
    },
    legend: {
      accessibility: {
        enabled: true,
        keyboardNavigation: { enabled: true },
      },
    },
    caption: title,
    series,
  };

  return <HighchartsReact highcharts={Highcharts} options={options} ref={highchartsRef} />;
}

MultiTargetChart.propTypes = {
  height: PropTypes.number,
  measureResults: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  advancedMetricTargetUnit: PropTypes.string,
  title: PropTypes.string.isRequired,
  advancedMetricChartType: PropTypes.string.isRequired,
  showCSVDownload: PropTypes.bool,
  onChartRef: PropTypes.func,
};
